/**
 * Implement detail page — shared between v1 and v2.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Cog, Edit2, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { EditEntityDialog } from "@/components/v2/dialogs/EditEntityDialog";
import { DeleteEntityDialog } from "@/components/v2/dialogs/DeleteEntityDialog";
import type { FieldDef } from "@/components/v2/dialogs/AddEntityDialog";
import { authFetch } from "@/lib/auth";

type ServiceStatus = "in_service" | "maintenance" | "out_of_service" | "retired";

interface Implement {
  id: string;
  ranch_id: string;
  name: string;
  asset_tag: string | null;
  model_number: string | null;
  serial_number: string | null;
  service_status: ServiceStatus | null;
  hour_meter_value: number | null;
  acquisition_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<ServiceStatus, string> = {
  in_service: "In Service",
  maintenance: "Maintenance",
  out_of_service: "Out of Service",
  retired: "Retired",
};
const STATUS_TONE: Record<ServiceStatus, string> = {
  in_service: "bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark",
  maintenance: "bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark",
  out_of_service: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
  retired: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
};

const FIELDS: FieldDef[] = [
  { name: "name", label: "Name", required: true, placeholder: "Hay baler" },
  {
    name: "service_status",
    label: "Service status",
    type: "select",
    half: true,
    options: (Object.keys(STATUS_LABEL) as ServiceStatus[]).map((v) => ({
      value: v,
      label: STATUS_LABEL[v],
    })),
  },
  { name: "asset_tag", label: "Asset tag", half: true, placeholder: "IMP-001" },
  { name: "model_number", label: "Model #", half: true },
  { name: "serial_number", label: "Serial #", half: true },
  { name: "hour_meter_value", label: "Hour meter", type: "number", placeholder: "0" },
  { name: "notes", label: "Notes", type: "textarea" },
];

function transformImplement(v: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (v.name !== undefined) out.name = v.name;
  if (v.service_status !== undefined) out.service_status = v.service_status || "in_service";
  if (v.asset_tag !== undefined) out.asset_tag = v.asset_tag || null;
  if (v.model_number !== undefined) out.model_number = v.model_number || null;
  if (v.serial_number !== undefined) out.serial_number = v.serial_number || null;
  if (v.hour_meter_value !== undefined)
    out.hour_meter_value = v.hour_meter_value ? Number(v.hour_meter_value) : null;
  if (v.notes !== undefined) out.notes = v.notes || null;
  return out;
}

function statusOf(i: Implement): ServiceStatus {
  return i.service_status ?? "in_service";
}

export function ImplementDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [implement, setImplement] = useState<Implement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const data = await authFetch<Implement>(`/api/implements/${id}`);
      setImplement(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load implement");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load, refreshTick]);

  if (loading) {
    return (
      <>
        <PageHeader
          title=""
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Implements", href: "/implements" },
            { label: "Loading…" },
          ]}
        />
        <Skeleton className="h-48 w-full rounded-lg" />
      </>
    );
  }

  if (error || !implement) {
    return (
      <>
        <PageHeader
          title="Implement Not Found"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Implements", href: "/implements" },
            { label: "Error" },
          ]}
        />
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
          {error || "Implement not found"}
        </div>
      </>
    );
  }

  const status = statusOf(implement);

  return (
    <>
      <PageHeader
        title={implement.name}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Implements", href: "/implements" },
          { label: implement.name },
        ]}
      >
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Edit2 className="mr-1 h-4 w-4" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteOpen(true)}
          className="text-destructive border-destructive/30"
        >
          <Trash2 className="mr-1 h-4 w-4" /> Archive
        </Button>
      </PageHeader>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Cog className="h-5 w-5 text-ranch-brown" /> Implement Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Service status</p>
                <p className="mt-1">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[status]}`}
                  >
                    {STATUS_LABEL[status]}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Asset tag</p>
                <p className="font-medium font-mono text-sm">{implement.asset_tag ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Model #</p>
                <p className="font-medium">{implement.model_number ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Serial #</p>
                <p className="font-medium font-mono text-sm">{implement.serial_number ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Hour meter</p>
                <p className="font-medium">
                  {implement.hour_meter_value != null
                    ? implement.hour_meter_value.toLocaleString()
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Acquired</p>
                <p className="font-medium">{implement.acquisition_date ?? "—"}</p>
              </div>
              {implement.notes ? (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{implement.notes}</p>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <EntityImageGallery entityType="implement" entityId={implement.id} />
          </CardContent>
        </Card>
      </div>

      <EditEntityDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title={`Edit ${implement.name}`}
        endpoint="/api/implements"
        entityId={implement.id}
        fields={FIELDS}
        initialValues={{
          name: implement.name,
          service_status: status,
          asset_tag: implement.asset_tag ?? "",
          model_number: implement.model_number ?? "",
          serial_number: implement.serial_number ?? "",
          hour_meter_value:
            implement.hour_meter_value != null ? String(implement.hour_meter_value) : "",
          notes: implement.notes ?? "",
        }}
        transform={transformImplement}
        onSaved={() => setRefreshTick((t) => t + 1)}
      />
      <DeleteEntityDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        endpoint="/api/implements"
        entityId={implement.id}
        entityName={implement.name}
        onDeleted={() => navigate("/implements")}
      />
    </>
  );
}
